<template>
  <Card>
    <CardHeader>
      <CardTitle>4. Offer details</CardTitle>
      <CardDescription>
        Supplier, display information and the logo shown to customers.
      </CardDescription>
    </CardHeader>

    <CardContent class="space-y-6">
      <div class="grid gap-4 sm:grid-cols-2">
        <div class="space-y-1.5">
          <Label>Offer name</Label>
          <Input v-model="info.offerName" placeholder="e.g. 5% cashback at Acme" />
        </div>
        <div class="space-y-1.5">
          <Label>Internal offer ID</Label>
          <Input v-model="info.internalOfferId" placeholder="Your own reference (optional)" />
          <p class="text-xs text-muted-foreground">
            Your own reference for this offer (optional).
          </p>
        </div>
        <div class="space-y-1.5">
          <Label>Offer type</Label>
          <Select v-model="info.offerType">
            <SelectTrigger class="w-full">
              <SelectValue placeholder="Select a type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="t in offerTypes" :key="t" :value="t">{{
                formatType(t)
              }}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="space-y-1.5">
          <Label>Country</Label>
          <Select v-model="info.country">
            <SelectTrigger class="w-full">
              <SelectValue placeholder="Select a country" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="c in countries" :key="c" :value="c">
                {{ c === 'GLOBAL' ? 'Global (all countries)' : c }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="space-y-1.5">
          <Label>Currency</Label>
          <Input v-model="currencyInput" placeholder="e.g. GBP" />
        </div>
        <div class="space-y-1.5">
          <Label>Offer length (days)</Label>
          <Input v-model.number="info.lengthOfTime" type="number" placeholder="e.g. 30" />
        </div>
        <div class="space-y-1.5">
          <Label>Website</Label>
          <Input v-model="info.link" placeholder="https://example.com" />
        </div>
      </div>

      <div class="space-y-1.5">
        <Label>Description</Label>
        <Textarea v-model="info.offerDescription" placeholder="Describe the offer for customers" />
      </div>

      <div class="space-y-1.5">
        <Label>Terms &amp; conditions</Label>
        <Textarea
          v-model="info.offerTerms"
          placeholder="Any terms customers should know (optional)"
        />
      </div>

      <!-- Categories -->
      <div class="space-y-1.5">
        <Label>Categories</Label>
        <div class="flex flex-wrap gap-1.5">
          <Badge
            v-for="cat in categories"
            :key="cat"
            :variant="info.categories.includes(cat) ? 'default' : 'outline'"
            class="cursor-pointer"
            @click="toggleCategory(cat)"
          >
            {{ formatType(cat) }}
          </Badge>
        </div>
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <div class="space-y-1.5">
          <Label>Offer logo</Label>
          <input
            type="file"
            accept="image/*"
            aria-label="Offer logo"
            class="block w-full cursor-pointer text-sm text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground file:transition-colors hover:file:bg-primary/90"
            @change="onLogoChange"
          />
          <div v-if="logoPreview" class="flex items-center gap-2 pt-1">
            <img
              :src="logoPreview"
              class="object-contain w-12 h-12 border border-border bg-white rounded-lg"
            />
            <span class="text-xs text-muted-foreground">Logo preview</span>
          </div>

          <div class="space-y-1.5 pt-3">
            <Label>Banner image (optional)</Label>
            <input
              type="file"
              accept="image/*"
              aria-label="Banner image"
              class="block w-full cursor-pointer text-sm text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground file:transition-colors hover:file:bg-primary/90"
              @change="onBannerChange"
            />
            <Input
              v-model="info.offerBanner"
              placeholder="…or paste a hosted image URL"
              @update:model-value="onBannerUrlInput"
            />
            <div v-if="bannerPreview || info.offerBanner" class="pt-1">
              <img
                :src="bannerPreview || info.offerBanner"
                class="max-h-24 rounded-lg border border-border object-contain"
              />
            </div>
          </div>
        </div>

        <div class="space-y-3">
          <div class="space-y-1.5">
            <Label>Match against</Label>
            <Select v-model="info.matchAgainst">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL_CHANNELS">All channels</SelectItem>
                <SelectItem value="SPECIFIC_CHANNELS">Specific channels</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label class="flex items-center gap-2 text-sm">
            <Switch v-model="info.superMatch" /> Super match
          </label>
          <div v-if="info.superMatch" class="space-y-1.5">
            <Label>Super-match descriptors</Label>
            <p class="text-xs text-muted-foreground">
              Creditor / DBA strings used to match transactions.
            </p>
            <div class="flex gap-2">
              <Input
                v-model="creditorInput"
                placeholder="Enter a descriptor and press Add"
                @keyup.enter="addCreditor"
              />
              <Button variant="secondary" type="button" class="shrink-0" @click="addCreditor">
                Add
              </Button>
              <Button
                variant="outline"
                type="button"
                class="shrink-0"
                @click="$refs.creditorFile.click()"
              >
                <Upload class="w-4 h-4" />
              </Button>
              <input
                ref="creditorFile"
                type="file"
                accept=".csv,.txt"
                class="hidden"
                aria-label="Upload descriptors"
                @change="onCreditorFile"
              />
            </div>
            <div
              v-if="info.unenrichedCreditorsStrings.length"
              class="flex flex-wrap gap-1.5 pt-1"
            >
              <Badge
                v-for="(s, i) in info.unenrichedCreditorsStrings"
                :key="s + i"
                variant="outline"
                class="gap-1 cursor-pointer"
                @click="removeCreditor(i)"
              >
                {{ s }}
                <X class="w-3 h-3" />
              </Badge>
            </div>
          </div>
          <label class="flex items-center gap-2 text-sm">
            <Switch v-model="info.spendingCap" /> Spending cap
          </label>
          <div v-if="info.spendingCap" class="space-y-1.5">
            <Label>Spending cap amount</Label>
            <Input v-model.number="info.spendingCapAmount" type="number" placeholder="0.00" />
          </div>
          <label class="flex items-center gap-2 text-sm">
            <Switch v-model="info.hidden" /> Hidden offer
          </label>
          <label class="flex items-center gap-2 text-sm">
            <Switch v-model="info.customReporting" /> Custom reporting
          </label>
          <div v-if="info.customReporting" class="space-y-1.5">
            <Label>Reporting period</Label>
            <Select v-model="info.reportingPeriod">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Select a period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="r in reportingPeriods" :key="r" :value="r">{{
                  titleCase(r)
                }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label class="flex items-center gap-2 text-sm">
            <Switch v-model="info.customRefundPeriod" /> Custom refund period
          </label>
          <div v-if="info.customRefundPeriod" class="space-y-1.5">
            <Label>Refund period (days)</Label>
            <Input v-model.number="info.refundPeriodInDays" type="number" placeholder="e.g. 30" />
          </div>
        </div>
      </div>

      <details class="rounded-lg border border-border p-3">
        <summary class="cursor-pointer text-sm font-medium">Offer page content (optional)</summary>
        <p class="mt-1 text-xs text-muted-foreground">
          Rich content shown on the offer's brand page in the whitelabel storefront. Paste hosted
          image URLs for any images.
        </p>

        <div class="mt-3 space-y-4">
          <div class="space-y-1.5">
            <Label>Section title</Label>
            <Input v-model="info.sectionTitle" placeholder="e.g. Shop our services" />
            <Textarea v-model="info.sectionDescription" placeholder="Short intro under the title" />
          </div>

          <div class="space-y-1.5">
            <Label>CTA banner</Label>
            <Input v-model="info.ctaBanner.image" placeholder="Image URL" />
            <Input v-model="info.ctaBanner.link" placeholder="Link (https://…)" />
            <Input v-model="info.ctaBanner.buttonText" placeholder="Button text" />
          </div>

          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <Label>CTA tiles</Label>
              <Button type="button" variant="outline" size="sm" @click="addTile">Add tile</Button>
            </div>
            <div
              v-for="(tile, i) in info.ctaTiles"
              :key="i"
              class="space-y-1.5 rounded-md border border-border p-2.5"
            >
              <div class="flex items-center justify-between">
                <span class="text-xs text-muted-foreground">Tile {{ i + 1 }}</span>
                <Button type="button" variant="ghost" size="sm" @click="info.ctaTiles.splice(i, 1)">
                  Remove
                </Button>
              </div>
              <Input v-model="tile.image" placeholder="Image URL" />
              <Input v-model="tile.link" placeholder="Link (https://…)" />
              <Input v-model="tile.buttonText" placeholder="Button text" />
              <Input v-model="tile.description" placeholder="Description" />
            </div>
          </div>

          <div class="space-y-1.5">
            <Label>Video section</Label>
            <Input
              v-model="info.videoSectionTitle"
              placeholder="Title (e.g. What makes us great?)"
            />
            <Textarea
              v-model="info.videoSectionDescription"
              placeholder="Short intro under the title"
            />
            <Input
              v-model="info.videoCta.videoUrl"
              placeholder="Video URL (YouTube / Vimeo / mp4)"
            />
            <Input v-model="info.videoCta.posterImage" placeholder="Poster image URL" />
            <Input v-model="info.videoCta.link" placeholder="Button link (https://…)" />
            <Input v-model="info.videoCta.buttonText" placeholder="Button text" />
          </div>
        </div>
      </details>

      <p v-if="error" class="text-sm text-destructive">{{ error }}</p>
    </CardContent>

    <CardFooter class="justify-between gap-2">
      <Button variant="outline" @click="back"> <MoveLeft class="w-4 h-4" /> Back </Button>
      <Button :disabled="!valid" @click="next">
        Next stage
        <MoveRight class="w-4 h-4" />
      </Button>
    </CardFooter>
  </Card>
</template>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
  Input,
  Textarea,
  Label,
  Badge,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@boogi/ui'
import { MoveRight, MoveLeft, Upload, X } from 'lucide-vue-next'
import { addOfferWizard } from '@/stores/addOfferWizard'

export default {
  name: 'step-offer-info',
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
    Button,
    Input,
    Textarea,
    Label,
    Badge,
    Switch,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    MoveRight,
    MoveLeft,
    Upload,
    X,
  },
  data() {
    return {
      wizard: addOfferWizard,
      info: addOfferWizard.offerInformation,
      offerTypes: ['PAYMENT', 'ONLINE', 'CLICK_TO_ACTIVATE'],
      countries: ['GLOBAL', 'GB', 'US', 'FR', 'DE', 'IE'],
      categories: [
        'HEALTH_BEAUTY',
        'ELECTRONICS',
        'FASHION',
        'HOME',
        'LUXURY',
        'SPORTS',
        'TRAVEL',
        'DIGITAL',
        'ACCESSORIES',
        'FOOD_DRINK',
        'SHOPPING',
        'WELLNESS',
        'ENTERTAINMENT',
        'ESSENTIALS',
      ],
      currencyInput: '',
      creditorInput: '',
      logoPreview: '',
      bannerPreview: '',
      reportingPeriods: ['DAILY', 'WEEKLY', 'BI_WEEKLY', 'MONTHLY', 'MONDAY'],
      error: '',
    }
  },
  computed: {
    valid() {
      return (
        this.info.offerName.trim() !== '' &&
        this.info.offerType !== '' &&
        this.info.offerDescription.trim() !== '' &&
        this.info.offerLogoFile !== undefined
      )
    },
  },
  created() {
    if (!this.wizard.channelsConfirmed) {
      this.$router.replace('/add-offer/retailer')
      return
    }
    this.currencyInput = (this.info.currency || []).join(', ')
    this.logoPreview = this.info.offerLogo || ''
  },
  methods: {
    formatType(value) {
      if (!value) return ''
      return value
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },
    toggleCategory(cat) {
      const i = this.info.categories.indexOf(cat)
      if (i === -1) this.info.categories.push(cat)
      else this.info.categories.splice(i, 1)
    },
    onLogoChange(event) {
      const file = event.target.files[0]
      if (!file) return
      this.info.offerLogoFile = file
      this.logoPreview = URL.createObjectURL(file)
      this.info.offerLogo = this.logoPreview
    },
    // Banner and its URL field are mutually exclusive: a picked file supersedes any URL.
    onBannerChange(event) {
      const file = event.target.files[0]
      if (!file) return
      this.info.offerBannerFile = file
      this.bannerPreview = URL.createObjectURL(file)
      this.info.offerBanner = ''
    },
    onBannerUrlInput(val) {
      if (val) {
        this.info.offerBannerFile = undefined
        this.bannerPreview = ''
      }
    },
    addTile() {
      this.info.ctaTiles.push({ image: '', link: '', buttonText: '', description: '' })
    },
    addCreditor() {
      const s = this.creditorInput.trim()
      if (s && !this.info.unenrichedCreditorsStrings.includes(s)) {
        this.info.unenrichedCreditorsStrings.push(s)
      }
      this.creditorInput = ''
    },
    removeCreditor(i) {
      this.info.unenrichedCreditorsStrings.splice(i, 1)
    },
    // Bulk-add descriptor strings from an uploaded .csv/.txt file
    // (one per line or comma/semicolon separated). Trims, drops empties, dedupes.
    async onCreditorFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = '' // allow re-uploading the same file
      if (!file) return
      try {
        const text = await file.text()
        const parsed = text
          .split(/[\r\n,;]+/)
          .map((s) => s.trim())
          .filter(Boolean)
        for (const s of parsed) {
          if (!this.info.unenrichedCreditorsStrings.includes(s)) {
            this.info.unenrichedCreditorsStrings.push(s)
          }
        }
      } catch (e) {
        this.error = 'Could not read that file.'
      }
    },
    titleCase(v) {
      return String(v)
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },
    back() {
      this.$router.push('/add-offer/mids')
    },
    next() {
      if (!this.valid) return
      this.info.currency = this.currencyInput
        .split(',')
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean)
      this.$router.push('/add-offer/rules')
    },
  },
}
</script>
